import { CAP, can, canAll } from '@/lib/can'
import type { CaseDocument, CurrentUser } from '@/types/domain'

/* Who may ISSUE a case's document, per kind.
 *
 * Spelled once because two surfaces ask it — the strip on the invoice page and
 * the Money page's sheet (`DocumentActions`), and the row actions in the case's
 * «Счета» section (`DocumentRowActions`, told by its parent) — and the two
 * kinds do not ask the same question. The server's finance service refuses an
 * invoice's issue without BOTH the render and the issue key
 * (`finance.Service.IssueInvoice`), and a debit note's without the issue key
 * alone (`finance.Service.IssueDebitNote`). A copy of this branch at each call
 * site is how one of them ends up offering Issue to a reader the server will
 * refuse.
 *
 * An affordance, never a control: the server re-checks on the request (AD-21).
 */
export function mayIssueDocument(
  user: CurrentUser | null | undefined,
  kind: CaseDocument['kind'],
): boolean {
  return kind === 'invoice'
    ? canAll(user, CAP.documentInvoiceRender, CAP.documentInvoiceIssue)
    : can(user, CAP.documentDebitNoteIssue)
}

/* Who may OPEN an archived document's bytes, per kind.
 *
 * The client's mirror of `readCapability` (backend/internal/document/
 * permissions.go), which `GET /api/documents/:id/preview` asks after the row
 * scope. Finance's render keys are also its read keys: an invoice or debit-note
 * PDF prints the F-marked bank block and the debit note's recipient, so it
 * opens for the render key's holder alone (AD-6, #52). An offer and a status
 * update print neither and open on the archive key.
 *
 * `kind` is the wire's string rather than a closed union, and that is what the
 * default is for: the server's switch is total with a CLOSED default, and so is
 * this one — a kind added on the server before this build knows it is offered
 * to nobody, and it is decided BEFORE `can()`, so the superuser's short-circuit
 * cannot turn an unknown kind into an open door.
 *
 * An affordance, never a control: the server re-checks on the request (AD-21).
 */
export function mayOpenArchivedKind(
  user: CurrentUser | null | undefined,
  kind: string,
): boolean {
  switch (kind) {
    case 'invoice':
      return can(user, CAP.documentInvoiceRender)
    case 'debit-note':
      return can(user, CAP.documentDebitNoteRender)
    case 'offer':
    case 'status-update':
      return can(user, CAP.documentArchiveRead)
    default:
      return false
  }
}
